import { useMemo, useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHeader from "@/components/base/PageHeader";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilterSelect from "@/components/base/FilterSelect";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import ContentCard from "@/components/feature/ContentCard";
import { newsItems, sortByDate } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 6;
const REFERENCE_DATE = new Date("2026-10-14");

const rangeOptions = [
  { value: "all", label: "全部时间" },
  { value: "7", label: "近一周" },
  { value: "30", label: "近一月" },
  { value: "90", label: "近三月" },
];

const hotOptions = [
  { value: "all", label: "全部热度" },
  { value: "hot", label: "热门优先" },
];

export default function NewsPage() {
  usePageMeta({
    title: "动漫资讯 - 星空动漫",
    description:
      "星空动漫资讯频道实时更新新番情报、剧场版动态、行业新闻与展会活动，按发布时间与热度筛选，快速掌握动漫圈最新动态。",
    keywords: "动漫资讯,新番情报,剧场版,动漫新闻,行业动态,星空动漫",
  });

  const [range, setRange] = useState("all");
  const [hot, setHot] = useState("all");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    let list = sortByDate(newsItems);
    if (range !== "all") {
      const days = Number(range);
      const threshold = new Date(REFERENCE_DATE);
      threshold.setDate(threshold.getDate() - days);
      list = list.filter((item) => new Date(item.date) >= threshold);
    }
    if (hot === "hot") {
      list = list.filter((item) => item.popularity >= 80);
    }
    return list;
  }, [range, hot]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setRange("all");
    setHot("all");
    setPage(1);
  };

  return (
    <SiteLayout>
      <div className="mx-auto max-w-page px-4 py-8 md:px-6">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "动漫资讯" }]} />

        <div className="mt-4">
          <PageHeader
            title="动漫资讯"
            description="汇总新番定档、剧场版票房、行业报告与展会活动等一手动态，按时间与热度筛选，帮你快速了解动漫圈正在发生什么。"
          />
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-4 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
          <span className="flex items-center gap-1 text-sm font-medium text-foreground-700">
            <i className="ri-filter-3-line" />
            筛选
          </span>
          <FilterSelect
            label="时间范围"
            value={range}
            options={rangeOptions}
            onChange={(value) => {
              setRange(value);
              setPage(1);
            }}
          />
          <FilterSelect
            label="热度"
            value={hot}
            options={hotOptions}
            onChange={(value) => {
              setHot(value);
              setPage(1);
            }}
          />
          <span className="ml-auto text-sm text-foreground-500">
            共 <strong className="text-primary-600">{filtered.length}</strong> 条资讯
          </span>
        </div>

        <div className="mt-6">
          {pageItems.length > 0 ? (
            <div className="flex flex-col gap-4">
              {pageItems.map((item) => (
                <ContentCard key={item.id} item={item} variant="row" />
              ))}
            </div>
          ) : (
            <EmptyState
              icon="ri-file-search-line"
              title="没有符合条件的资讯"
              description="试着放宽时间范围或取消热度筛选，看看其他内容。"
              actionLabel="重置筛选"
              onAction={resetFilters}
            />
          )}
        </div>

        {pageItems.length > 0 ? (
          <div className="mt-8">
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </div>
        ) : null}
      </div>
    </SiteLayout>
  );
}